import {
  StructuredTableKind,
  UnknownStructuredTable,
} from 'notebook/structured-tables/StructuredTableConstants';
import {EvaluatedCell} from 'notebook/tables/excess/Evaluator';

import {getStructuredTableAdapters} from '../StructuredTableAdapters';
import buildRowUiData from './buildRowUiData';
import {getLiveRowMetadataFromRowUpdater} from './getLiveRowMetadataFromRowUpdater';

/**
 * Returns true if the Structured Table's computed equivalent contains any
 * un-submitted changes, i.e. values which are different than the
 * `lastSyncedUpstreamValues` which are stored on the table's rows. Will also
 * return true if there are rows without a lastSyncedUpstreamValue, which would
 * indicate that it's a new row.
 *
 * This function should be generous in what it considers "unsubmitted" as we
 * want to default to showing the "submit" button and other dependent functions,
 * even if they end up in a no-op. So while all rows may be considered "in sync"
 * without any changes, this function may still return that the table has
 * unsubmitted changes.
 *
 * @param structuredTable - the StructuredTable we're checking for changes
 * @param computedTable - the evaluated version of the StructuredTable
 */
export default function tableHasUnsubmittedChanges(
  structuredTable: UnknownStructuredTable,
  computedTable: Array<Array<EvaluatedCell>>
): boolean {
  // Lookup tables do not have a submit button
  const isLookupTable = structuredTable.kind === StructuredTableKind.LOOKUP;
  if (isLookupTable) {
    return false;
  }

  // Pull in live data to check for archival reasons that override unsubmitted changes
  const adapter = getStructuredTableAdapters()[structuredTable.kind];
  const primarySamplesByRowIds = adapter.getPrimarySamples(structuredTable, computedTable);
  const liveRowMetadataByRow = structuredTable.rows.map((row, i) =>
    getLiveRowMetadataFromRowUpdater({
      row,
      primarySamples: primarySamplesByRowIds[row.rowId],
      adapter,
      columns: structuredTable.columns,
    })
  );
  const liveRowUiData = buildRowUiData(structuredTable, liveRowMetadataByRow);
  const unsubmittedStatuses = adapter.getRowUnsubmittedStatuses(structuredTable, computedTable);

  // We check if the lastSyncedUpstreamValue is undefined and short circuit in
  // those cases as the rowHasUnsubmittedChanges function will always return
  // false when we haven't synced any values to avoid unnecessarily showing the
  // unsubmitted indicator when the table is new.
  return structuredTable.rows.some(
    (row, rowIndex) =>
      row.lastSyncedUpstreamValues === undefined ||
      (liveRowUiData[rowIndex].archivePurpose == null && unsubmittedStatuses[rowIndex])
  );
}
